import { HOME_WIDGET_QUERY_META } from "@/lib/home/widgets/query-lifecycle";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import type { QueuesResponse } from "@/lib/admin-queues";
import type { UserListCounts } from "@/lib/admin-users/types";
import type { JournalRange, QuarantineStats } from "@/lib/home/widgets/admin";
import { montrealOffsetMs } from "@/lib/montreal-time";
import { activitySummaryQueryOptions, type ActivityScope } from "@/lib/queries/activity";
import { taskRunsActivityQueryOptions } from "@/lib/queries/task-runs";

/**
 * Platform-health reads shared by the admin pages, the admin home widgets and
 * the suggestion wizard. Every endpoint here answers administrators only.
 */

/** The API envelope's French message, else our own. */
async function failure(response: Response, fallback: string): Promise<Error> {
  const parsed = z
    .object({ message: z.string() })
    .safeParse(await response.json().catch(() => null));

  return new Error(parsed.success ? parsed.data.message : fallback);
}

/** BullMQ counts for the five queues; `redisAvailable` is false when Redis does not answer. */
export const adminQueuesQueryOptions = queryOptions({
  meta: HOME_WIDGET_QUERY_META,
  queryKey: ["admin", "queues"],
  queryFn: async ({ signal }): Promise<QueuesResponse> => {
    const response = await fetch("/api/admin/queues", { cache: "no-store", signal });

    if (!response.ok) throw await failure(response, "Impossible de charger les files d'attente.");

    // SAFETY: GET /api/admin/queues answers { redisAvailable, queues }, the QueuesResponse shape.
    return (await response.json()) as QueuesResponse;
  },
});

export interface QuarantineFileSummary {
  id: string;
  name: string;
  scanStatus: string | null;
  createdAt: string | null;
  uploader: { fullName: string; email: string | null } | null;
  share: { id: string; name: string } | null;
}

export interface QuarantineOverview {
  stats: QuarantineStats;
  /** The newest files in quarantine, for the widget's rows. */
  latest: QuarantineFileSummary[];
}

const LATEST_FILES = 6;

/** Counts by scan status over the whole quarantine, and its newest files. */
export const adminQuarantineOverviewQueryOptions = queryOptions({
  meta: HOME_WIDGET_QUERY_META,
  queryKey: ["admin", "quarantine", "overview"],
  queryFn: async ({ signal }): Promise<QuarantineOverview> => {
    const response = await fetch(`/api/admin/quarantine?limit=${LATEST_FILES}`, {
      cache: "no-store",
      signal,
    });

    if (!response.ok) throw await failure(response, "Impossible de charger la quarantaine.");

    // SAFETY: GET /api/admin/quarantine answers { data, stats, pagination } with these row fields.
    const body = (await response.json()) as {
      data: QuarantineFileSummary[];
      stats: QuarantineStats;
    };

    return { stats: body.stats, latest: body.data };
  },
  staleTime: 60_000,
});

export interface AccountsOverview {
  counts: UserListCounts;
  stats: { newThisMonth: number; recentlyActive: number };
}

/** The user list's head counts, read with a one-row page so no list is loaded. */
export const adminAccountsOverviewQueryOptions = queryOptions({
  meta: HOME_WIDGET_QUERY_META,
  queryKey: ["admin", "users", "overview-counts"],
  queryFn: async ({ signal }): Promise<AccountsOverview> => {
    const response = await fetch("/api/admin/users?limit=1", { cache: "no-store", signal });

    if (!response.ok) throw await failure(response, "Impossible de charger les comptes.");
    // SAFETY: GET /api/admin/users answers { counts, stats } alongside its page of rows.
    const body = (await response.json()) as AccountsOverview;

    return {
      counts: body.counts,
      stats: { newThisMonth: body.stats.newThisMonth, recentlyActive: body.stats.recentlyActive },
    };
  },
  staleTime: 5 * 60_000,
});

/** The journal page's own landing scope, so its chart and the widget share one cache entry. */
const JOURNAL_SCOPE: ActivityScope = { view: "important", search: "", filters: [] };

/** Journal volume by outcome over `range`, bucketed on Montreal days. */
export function adminJournalQueryOptions(range: JournalRange, now: number) {
  return {
    ...activitySummaryQueryOptions(JOURNAL_SCOPE, range, montrealOffsetMs(now)),
    meta: HOME_WIDGET_QUERY_META,
  };
}

/** The cron console's window: the widget reads the last 24 hours of the same answer. */
const TASK_ACTIVITY_HOURS = 48;

export const adminTaskActivityQueryOptions = {
  ...taskRunsActivityQueryOptions(TASK_ACTIVITY_HOURS),
  meta: HOME_WIDGET_QUERY_META,
};
